import {
	Box,
	Drawer,
	Fab,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import QueueIcon from '@mui/icons-material/Queue';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useReducer, useRef, useState, useContext } from 'react';
import { i18n } from '../../../translate/i18n';
import CustomPagination from '../../General/CustomPagination';
import socket from '../../../hooks/useSocket';
import SkeletonList from '../../Skeleton/list';
import api from '../../../services/api';
import { toast } from 'react-toastify';
import toastError from '../../../errors/toastError';
import { AuthContext } from '../../../context/Auth/AuthContext';
import QueueSelectConnection from '../../Queue/queueSelectConnection';
import StoreModal from './storeModal';
import QueueModal from './queueModal';
import ConfirmationModal from '../../ConfirmationModal';
import {
	templateCategoryLabel,
	templateDateLabel,
	templateLanguageLabel,
	templateQualityLabel,
	templateStatusLabel,
} from './templateTableLabels';

const STATUS_FILTER_OPTIONS = [
	"APPROVED",
	"PENDING",
	"REJECTED",
	"PAUSED",
	"DISABLED",
	"IN_APPEAL",
	"PENDING_DELETION",
	"ARCHIVED",
];

const CATEGORY_FILTER_OPTIONS = [
	{ value: "UTILITY", labelKey: "connections.whatsapp.template.modal.categoryOptions.utility" },
	{ value: "MARKETING", labelKey: "connections.whatsapp.template.modal.categoryOptions.marketing" },
	{ value: "AUTHENTICATION", labelKey: "connections.whatsapp.template.modal.categoryOptions.authentication" },
];

export default function WhatsappTemplateTable({ ...props }) {

	const reducer = (state, action) => {

		if (action.type === "LOAD_WHATSAPP_TEMPLATES") {
			const whatsappTemplates = action.payload;
			const newWhatsappTemplates = [];

			whatsappTemplates.forEach((whatsappTemplate) => {
				newWhatsappTemplates.push(whatsappTemplate);
			});

			return [...newWhatsappTemplates].sort((a,b) => (a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase()) ? 1 : ((b.name.toLocaleLowerCase() > a.name.toLocaleLowerCase()) ? -1 : 0));
		}

		if (action.type === "UPDATE_WHATSAPP_TEMPLATES") {
			const whatsappTemplate = action.payload;
			const whatsappTemplateIndex = state.findIndex((t) => t.id == whatsappTemplate.id);

			if (whatsappTemplateIndex !== -1) {
				state[whatsappTemplateIndex] = whatsappTemplate;
				return [...state];
			} else {
				return [whatsappTemplate, ...state].sort((a,b) => (a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase()) ? 1 : ((b.name.toLocaleLowerCase() > a.name.toLocaleLowerCase()) ? -1 : 0));
			}
		}

		if (action.type === "DELETE_WHATSAPP_TEMPLATES") {
			const whatsappTemplateId = action.payload;
			return state.filter((t) => t.id !== whatsappTemplateId);
		}

		if (action.type === "RESET") {
			return [];
		}
	};

	const [whatsappTemplates, dispatch] = useReducer(reducer, []);
	const [searching, setSearching] = useState(false);
	const [filtersOpen, setFiltersOpen] = useState(false);
	const initialized = useRef(false);
	const skipFiltersEffect = useRef(true);
	const { user } = useContext(AuthContext);

	const confirmationModalInitialState = {
		id: "",
		open: false,
		action: "",
		title: "",
		message: "",
		name: "",
		whatsappId: null,
	};
	const updateModalInitialState = {
		open: false,
		data: null
	};
	const queueModalInitialState = {
		open: false,
		template: null,
	};
	const paginationInitialState = {
		total: 0,
		totalPages: 1,
		currentPage: 1,
		quantityPerPage: 10,
	};
	const filtersInitialState = {
		searchParam: "",
		status: "",
		category: "",
		enabled: "",
		connectionAndQueueIds: [],
	};

	const [confirmModalInfo, setConfirmModalInfo] = useState(confirmationModalInitialState);
	const [updateModalInfo, setUpdateModalInfo] = useState(updateModalInitialState);
	const [queueModalInfo, setQueueModalInfo] = useState(queueModalInitialState);
	const [pagination, setPagination] = useState(paginationInitialState);
	const [filters, setFilters] = useState(filtersInitialState);
	const [searchForParam, setSearchForParam] = useState(false);
	const paginationRef = useRef(pagination);
	const filtersRef = useRef(filters);

	useEffect(() => {
		paginationRef.current = pagination;
	}, [pagination]);

	useEffect(() => {
		filtersRef.current = filters;
	}, [filters]);

	const loadWhatsappTemplates = async (cPage, qttPerPage, nextFilters = filtersRef.current) => {
		setSearching(true);
		try {
			const params = {
				quantityPerPage: qttPerPage,
			};
			if (nextFilters?.searchParam) params.searchParam = nextFilters.searchParam;
			if (nextFilters?.status) params.status = nextFilters.status;
			if (nextFilters?.category) params.category = nextFilters.category;
			if (nextFilters?.enabled === "true" || nextFilters?.enabled === "false") {
				params.enabled = nextFilters.enabled;
			}
			if (Array.isArray(nextFilters?.connectionAndQueueIds) && nextFilters.connectionAndQueueIds.length > 0) {
				params.connectionAndQueueIds = JSON.stringify(nextFilters.connectionAndQueueIds);
			}

			const { data } = await api.get(`/whatsappTemplate/list/page/${cPage || 1}`, { params });
			if (data.whatsappTemplates) dispatch({ type: "LOAD_WHATSAPP_TEMPLATES", payload: data.whatsappTemplates });
			if (data?.currentPage && data?.total !== undefined && data?.totalPages) {
				setPagination({
					total: data.total,
					totalPages: data.totalPages,
					currentPage: data.currentPage,
					quantityPerPage: qttPerPage
				});
			}
		} catch (err) {
            toastError(err);
		} finally {
			setTimeout(() => {
				setSearching(false);
			}, 500);
		}
	}

	useEffect(() => {
		if (!initialized.current) {
			initialized.current = true;
			loadWhatsappTemplates(pagination.currentPage, pagination.quantityPerPage, filters);
		}

		const templateEventName = `whatsappTemplate-${user?.adminId}`;
		const templateEventFunction = async (data) => {

			if (data.action === "create") {
				const p = paginationRef.current;
				await loadWhatsappTemplates(p.currentPage, p.quantityPerPage);
			}

			if (data.action === "update") {
				if (data.whatsappTemplate?.dbId) {
					dispatch({ type: "UPDATE_WHATSAPP_TEMPLATES", payload: data.whatsappTemplate });
				} else {
					const p = paginationRef.current;
					await loadWhatsappTemplates(p.currentPage, p.quantityPerPage);
				}
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_WHATSAPP_TEMPLATES", payload: data.whatsappTemplateId });
			}
		};
		socket.on(templateEventName, templateEventFunction);

		return () => {
			socket.off(templateEventName, templateEventFunction);
		};
	}, [user?.adminId]);

	useEffect(() => {
		if (skipFiltersEffect.current) {
			skipFiltersEffect.current = false;
			return;
		}
		loadWhatsappTemplates(1, pagination.quantityPerPage, filters);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters.status, filters.category, filters.enabled, filters.connectionAndQueueIds, searchForParam]);

	const handleConfirmModalClose = (open) => {
		setConfirmModalInfo({
			...confirmationModalInitialState,
			open
		});
	}

	const handleConfirmModal = () => {
		// verificar qual rota deverá ser acionada
		switch (confirmModalInfo.action) {
			case 'delete':
				(async () => {
					try {
						const { data } = await api.post('/whatsappTemplate/remove', {
							id: confirmModalInfo.id,
							name: confirmModalInfo.name,
							whatsappId: confirmModalInfo.whatsappId,
						});
						setConfirmModalInfo(confirmationModalInitialState);
						await loadWhatsappTemplates(pagination.currentPage, pagination.quantityPerPage);
						toast.success(data?.message ? i18n.t(data.message) : i18n.t("generalMessages.SUCCESSFULLY_SAVED"));
					} catch (err) {
                        toastError(err);
					}
				})();
				break;
			default:
                toast.error(i18n.t('generalMessages.INTERNAL_ERROR'));
				break;
		}
	}

	const handleOpenEditTemplate = async (row) => {
		if (row?.editable === false) {
			toast.error(i18n.t("connections.whatsapp.template.table.notEditable"));
			return;
		}
		setUpdateModalInfo({
			open: true,
			data: {
				templateId: row?.id,
				whatsappId: row?.whatsappId,
			},
		});
	};

	const handleOpenDeleteTemplate = (row) => {
		setConfirmModalInfo({
			open: true,
			action: 'delete',
			title: i18n.t('connections.whatsapp.template.table.deleteTitle'),
			message: i18n.t('connections.whatsapp.template.table.deleteMessage', { name: row?.name || '' }),
			id: row?.id,
			name: row?.name,
			whatsappId: row?.whatsappId,
		});
	};

	const handleUpdateModalClose = () => {
		setUpdateModalInfo({
			open: false,
			data: null
		});
	}

	const handleOpenQueueModal = (row) => {
		setQueueModalInfo({
			open: true,
			template: row,
		});
	};

	const handleQueueModalClose = () => {
		setQueueModalInfo(queueModalInitialState);
	};

	const handleQueueSaved = (whatsappTemplate) => {
		if (whatsappTemplate) {
			dispatch({ type: "UPDATE_WHATSAPP_TEMPLATES", payload: whatsappTemplate });
		}
	};

	const handleToggleEnabled = async (row, enabled) => {
		if (!row?.dbId) return;
		try {
			const { data } = await api.post("/whatsappTemplate/toggleEnabled", {
				dbId: row.dbId,
				enabled,
			});
			if (data?.whatsappTemplate) {
				dispatch({ type: "UPDATE_WHATSAPP_TEMPLATES", payload: data.whatsappTemplate });
			}
		} catch (err) {
			toastError(err);
		}
	};

	const formatQueueNames = (queues) => {
		if (!queues || queues.length === 0) return i18n.t("connections.whatsapp.template.table.queuesAll");
		return queues.map((q) => q.name).join(", ");
	};

	const handlePagination = (event, values) => {
		const page = event.target.value || values;
		setPagination({...pagination, currentPage: page});
		loadWhatsappTemplates(page, pagination.quantityPerPage);
	}

	const handleQuantityPerPage = (event) => {
		const qtt = event?.target?.value || pagination.quantityPerPage;
		setPagination({ ...pagination, quantityPerPage: qtt });
		setTimeout(() => {
			loadWhatsappTemplates(pagination.currentPage, qtt);
		}, 500);
	}

	const handleSearch = (str) => {
		setFilters((prev) => ({ ...prev, searchParam: str }));
	};

	const triggerSearch = () => {
		setSearchForParam((prev) => !prev);
	};

	const renderFilters = () => (
		<>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography sx={{ mb: 1 }}>
						{i18n.t("connections.whatsapp.template.table.filters.search")}
					</Typography>
					<TextField
						placeholder={i18n.t("connections.whatsapp.template.table.filters.searchPlaceholder")}
						variant="outlined"
						size="small"
						type="search"
						value={filters.searchParam}
						fullWidth
						onChange={(e) => handleSearch(e.target.value)}
						onKeyUp={(e) => {
							if (e.target.value === "") {
								triggerSearch();
								return;
							}
							const key = e.charCode || e.keyCode || 0;
							if (key === 13) {
								e.preventDefault();
								triggerSearch();
							}
						}}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start" onClick={triggerSearch} sx={{ cursor: "pointer" }}>
									<Tooltip title={i18n.t("contacts.filters.searchTooltip")}>
										<SearchIcon sx={{ color: "gray" }} />
									</Tooltip>
								</InputAdornment>
							),
						}}
					/>
				</Grid>
			</Grid>
			<Grid container spacing={2} sx={{ mt: 0.5 }}>
				<Grid item xs={12}>
					<Typography sx={{ mb: 1 }}>
						{i18n.t("connections.whatsapp.template.table.filters.status")}
					</Typography>
					<FormControl fullWidth size="small">
						<Select
							displayEmpty
							value={filters.status}
							onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
						>
							<MenuItem value="">
								{i18n.t("connections.whatsapp.template.table.filters.all")}
							</MenuItem>
							{STATUS_FILTER_OPTIONS.map((status) => (
								<MenuItem key={status} value={status}>
									{templateStatusLabel(status)}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
			<Grid container spacing={2} sx={{ mt: 0.5 }}>
				<Grid item xs={12}>
					<Typography sx={{ mb: 1 }}>
						{i18n.t("connections.whatsapp.template.table.filters.category")}
					</Typography>
					<FormControl fullWidth size="small">
						<Select
							displayEmpty
							value={filters.category}
							onChange={(e) => setFilters((prev) => ({ ...prev, category: e.target.value }))}
						>
							<MenuItem value="">
								{i18n.t("connections.whatsapp.template.table.filters.all")}
							</MenuItem>
							{CATEGORY_FILTER_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{i18n.t(option.labelKey)}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
			<Grid container spacing={2} sx={{ mt: 0.5 }}>
				<Grid item xs={12}>
					<Typography sx={{ mb: 1 }}>
						{i18n.t("connections.whatsapp.template.table.filters.connection")}
					</Typography>
					<QueueSelectConnection
						selecteds={filters.connectionAndQueueIds}
						onSelected={(opt) => {
							setFilters((prev) => ({ ...prev, connectionAndQueueIds: opt }));
						}}
						isCollapsed={true}
						userId={user?.id || undefined}
						filterConnection={(conn) => conn.moduleId == 3}
					/>
				</Grid>
			</Grid>
			<Grid container spacing={2} sx={{ mt: 0.5 }}>
				<Grid item xs={12}>
					<Typography sx={{ mb: 1 }}>
						{i18n.t("connections.whatsapp.template.table.filters.enabled")}
					</Typography>
					<FormControl fullWidth size="small">
						<Select
							displayEmpty
							value={filters.enabled}
							onChange={(e) => setFilters((prev) => ({ ...prev, enabled: e.target.value }))}
						>
							<MenuItem value="">
								{i18n.t("connections.whatsapp.template.table.filters.all")}
							</MenuItem>
							<MenuItem value="true">
								{i18n.t("connections.whatsapp.template.table.filters.enabledActive")}
							</MenuItem>
							<MenuItem value="false">
								{i18n.t("connections.whatsapp.template.table.filters.enabledInactive")}
							</MenuItem>
						</Select>
					</FormControl>
				</Grid>
			</Grid>
		</>
	);

	const renderWhatsappTemplates = () => {
		let rows = [];
		if (searching == true) {
			rows.push(<TableRow key="00">
				<TableCell colSpan={9}>
					<SkeletonList rows={5} />
				</TableCell>
			</TableRow>);
		} else {
			(whatsappTemplates && whatsappTemplates.length >= 1) ? 
                    whatsappTemplates.map(whatsappTemplate => {
                        rows.push(
                            <TableRow key={`${whatsappTemplate.whatsappId}-${whatsappTemplate.id}`}>
                                <TableCell align="left">
                                    <Tooltip title={whatsappTemplate?.apiName || whatsappTemplate?.name || ""}>
                                        <span>{whatsappTemplate?.displayName || whatsappTemplate?.name}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="left">{templateStatusLabel(whatsappTemplate?.status)}</TableCell>
                                <TableCell align="left">{templateCategoryLabel(whatsappTemplate?.category)}</TableCell>
                                <TableCell align="left">
                                    <Typography component="span" sx={{ display: "block" }}>
                                        {templateLanguageLabel(whatsappTemplate?.language)}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{ display: "block", fontSize: "0.75rem", color: "text.secondary", mt: 0.5 }}
                                        title={whatsappTemplate?.preview}
                                    >
                                        {whatsappTemplate?.preview || "-"}
                                    </Typography>
                                </TableCell>
                                <TableCell align="left">{templateQualityLabel(whatsappTemplate?.qualityRating)}</TableCell>
                                <TableCell align="left">{templateDateLabel(whatsappTemplate?.lastUpdated)}</TableCell>
                                <TableCell align="center">
									<Tooltip title={i18n.t("connections.whatsapp.template.table.frontEnabled")}>
										<Switch
											size="small"
											checked={whatsappTemplate?.enabled !== false}
											onChange={(e) => handleToggleEnabled(whatsappTemplate, e.target.checked)}
											color="primary"
										/>
									</Tooltip>
                                </TableCell>
                                <TableCell align="left" sx={{ overflow: "visible !important" }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
										<Tooltip title={formatQueueNames(whatsappTemplate?.queues)}>
											<Typography
												component="span"
												noWrap
												sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
											>
												{formatQueueNames(whatsappTemplate?.queues)}
											</Typography>
										</Tooltip>
										<Tooltip title={i18n.t("connections.whatsapp.template.table.configureQueues")}>
											<IconButton
												size="small"
												onClick={() => handleOpenQueueModal(whatsappTemplate)}
												aria-label="configure-queues"
												sx={{ flexShrink: 0 }}
											>
												<QueueIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</Box>
                                </TableCell>
                                <TableCell align="right">
									<Tooltip title={i18n.t('connections.whatsapp.template.table.edit')}>
										<span>
											<IconButton
												size="small"
												onClick={() => handleOpenEditTemplate(whatsappTemplate)}
												aria-label="edit-template"
												disabled={whatsappTemplate?.editable === false}
											>
												<EditIcon />
											</IconButton>
										</span>
									</Tooltip>
									<Tooltip title={i18n.t('connections.whatsapp.template.table.delete')}>
										<IconButton
											size="small"
											onClick={() => handleOpenDeleteTemplate(whatsappTemplate)}
											aria-label="delete-template"
										>
											<DeleteOutlineIcon />
										</IconButton>
									</Tooltip>
                                </TableCell>
                            </TableRow>
                        );
                    })
                :
                    rows.push(
                        <TableRow key="notFoundKey">
                            <TableCell colSpan={9} align="center">{i18n.t('connections.whatsapp.template.table.notFound')}</TableCell>
                        </TableRow>
                    );
		}

		return rows;
	}

	return (
		<>
			<Grid container spacing={2} mb={2} mt={2}>
				<Grid item xs={12} lg={10}>
					<TableContainer component={Paper} {...props} >
						<Table
							sx={{
								minWidth: 500,
								'& td, & th': { maxWidth: "220px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }
							}}
							aria-label="simple table"
						>
							<TableHead>
								<TableRow>
									<TableCell align="left">{i18n.t('connections.whatsapp.template.table.name')}</TableCell>
                                    <TableCell align="left">{i18n.t('connections.whatsapp.template.table.status')}</TableCell>
                                    <TableCell align="left">{i18n.t('connections.whatsapp.template.table.category')}</TableCell>
                                    <TableCell align="left">{i18n.t('connections.whatsapp.template.table.language')}</TableCell>
                                    <TableCell align="left">{i18n.t('connections.whatsapp.template.table.qualityRating')}</TableCell>
                                    <TableCell align="left">{i18n.t('connections.whatsapp.template.table.lastUpdated')}</TableCell>
                                    <TableCell align="center">{i18n.t('connections.whatsapp.template.table.frontStatus')}</TableCell>
                                    <TableCell align="left">{i18n.t('connections.whatsapp.template.table.queues')}</TableCell>
									<TableCell align="right">{i18n.t('connections.whatsapp.template.table.actions')}</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{renderWhatsappTemplates()}
							</TableBody>
						</Table>
					</TableContainer>
					<Grid container spacing={2} mb={2} mt={2}>
						<Grid item xs={12} sm={11}>
							<CustomPagination
								totalPages={pagination?.totalPages || 1}
								currentPage={pagination?.currentPage || 1}
								onChange={handlePagination}
							/>
						</Grid>
						<Grid item xs={12} sm={1}>
							<FormControl fullWidth>
								<InputLabel id="qttPerPageLabel">{i18n.t('connections.whatsapp.template.table.filters.quantity')}</InputLabel>
								<Select
									labelId="qttPerPageLabel"
									id="qttPerPage"
									value={pagination?.quantityPerPage || 10}
									label={i18n.t('connections.whatsapp.template.table.filters.quantity')}
									onChange={handleQuantityPerPage}
									sx={{height: "40px"}}
								>
									<MenuItem value={10}>10</MenuItem>
									<MenuItem value={20}>20</MenuItem>
									<MenuItem value={50}>50</MenuItem>
								</Select>
							</FormControl>
						</Grid>
					</Grid>
				</Grid>
				<Grid
					item
					xs={12}
					lg={2}
					sx={{
						display: { xs: "none", lg: "block" },
					}}
				>
					{renderFilters()}
				</Grid>
			</Grid>
			<Drawer
				anchor="left"
				open={filtersOpen}
				onClose={() => setFiltersOpen(false)}
				PaperProps={{ sx: { width: 300, p: 1.5 } }}
			>
				{renderFilters()}
			</Drawer>
			<Tooltip title={i18n.t("ticketManagement.filters.title")}>
				<Fab
					color="primary"
					aria-label="filters"
					size="small"
					onClick={() => setFiltersOpen(true)}
					sx={{
						position: "fixed",
						bottom: 16,
						right: 16,
						display: { xs: "flex", lg: "none" },
						zIndex: 1200,
					}}
				>
					<FilterListOutlinedIcon />
				</Fab>
			</Tooltip>
            <StoreModal 
                open={updateModalInfo.open}
                id={updateModalInfo?.data?.templateId || null}
                whatsappId={updateModalInfo?.data?.whatsappId || null}
                onClose={handleUpdateModalClose}
                onSaved={() => loadWhatsappTemplates(pagination.currentPage, pagination.quantityPerPage)}
            />
			<QueueModal
				open={queueModalInfo.open}
				template={queueModalInfo.template}
				onClose={handleQueueModalClose}
				onSaved={handleQueueSaved}
			/>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalInfo.open}
				onClose={(v) => handleConfirmModalClose(v)}
				onConfirm={handleConfirmModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
		</>
	);
}
